import {
  AirlineChoice,
  CashBackBigPurchase,
  CashBackPreference,
  CreditScore,
  CurrentBalanceCompany,
  HotelChoice,
  HowLongToPayOffDebt,
  IntroAPROrRewards,
  LuxuryPerks,
  ManagingCategories,
  PrimaryGoal,
  RewardsAndPerks,
  SpecificAirlineOrHotelValues,
  SpendingCategories,
  TypeOfDebt,
  StudentSpendingCategories,
  PurchaseSpendingCategories,
} from 'src/types/inputs';
import { Input } from 'src/lib/constants';
import { InputsReducer } from 'src/redux/reducers/inputs';
import { ExperimentsReducer } from 'src/redux/reducers/experiments';
import {
  isNewBuildCreditFlowExperimentActive,
  isShowInternationalPartnersExperimentsActive,
} from 'src/lib/experiments';

export type InputOptions = {
  value: string | boolean;
  label: string;
};
type InputProps = {
  options: InputOptions[];
  multiSelect?: boolean;
  maxSelectedOptions?: number;
};

type OptionsLookupValue =
  | InputProps
  | ((inputs: InputsReducer, experiments?: ExperimentsReducer) => InputProps)
  | undefined;
type OptionsLookup = Record<Input, OptionsLookupValue>;

const YES_NO_OPTIONS: InputProps = {
  options: [
    {
      value: true,
      label: 'Yes',
    },
    {
      value: false,
      label: 'No',
    },
  ],
};
const OPTIONS_LOOKUP: OptionsLookup = {
  [Input.AirlineChoice]: (inputs, experiments) => ({
    options: [
      {
        value: AirlineChoice.AmericanAirlines,
        label: 'American Airlines',
      },
      {
        value: AirlineChoice.AlaskaAirlines,
        label: 'Alaska Airlines',
      },
      {
        value: AirlineChoice.Delta,
        label: 'Delta',
      },
      {
        value: AirlineChoice.SouthwestAirlines,
        label: 'Southwest Airlines',
      },
      {
        value: AirlineChoice.UnitedAirlines,
        label: 'United Airlines',
      },
      ...displayInternationalAirlines(inputs, experiments),
      {
        value: AirlineChoice.Other,
        label: 'Other',
      },
    ],
  }),
  [Input.AnnualFees]: (inputs, experiments) => ({
    options: [
      {
        value: true,
        label: determineAnnualFeeAffirmativeOption(inputs, experiments),
      },
      {
        value: false,
        label: 'No',
      },
    ],
  }),
  [Input.CardDeposit]: YES_NO_OPTIONS,
  [Input.CollegeStudent]: YES_NO_OPTIONS,
  [Input.CreditScore]: (inputs, experiments) => ({
    options: [
      {
        value: CreditScore.Excellent,
        label: 'Excellent (720-850)',
      },
      {
        value: CreditScore.Good,
        label: 'Good (690-719)',
      },
      {
        value: CreditScore.Average,
        label: 'Fair (630-689)',
      },
      {
        value: CreditScore.Poor,
        label: 'Bad (350-629)',
      },
      ...(experiments !== undefined &&
      isNewBuildCreditFlowExperimentActive(experiments)
        ? [
            {
              value: CreditScore.LimitedCreditHistory,
              label: 'Limited Credit History \n (less than 3 years)',
            },
          ]
        : []),
    ],
  }),
  [Input.CurrentBalanceCompany]: (inputs, experiments) => ({
    options: [
      {
        value: CurrentBalanceCompany.AmericanExpress,
        label: 'American Express',
      },
      {
        value: CurrentBalanceCompany.BankOfAmerica,
        label: 'Bank of America',
      },
      {
        value: CurrentBalanceCompany.CapitalOne,
        label: 'Capital One',
      },
      {
        value: CurrentBalanceCompany.Chase,
        label: 'Chase',
      },
      {
        value: CurrentBalanceCompany.Citibank,
        label: 'Citi',
      },
      {
        value: CurrentBalanceCompany.Discover,
        label: 'Discover',
      },
      {
        value: CurrentBalanceCompany.UsBank,
        label: 'US Bank',
      },
      {
        value: CurrentBalanceCompany.WellsFargo,
        label: 'Wells Fargo',
      },
      {
        value: CurrentBalanceCompany.Other,
        label: 'Somewhere else',
      },
    ],
  }),
  [Input.HotelChoice]: {
    options: [
      {
        value: HotelChoice.Choice,
        label: 'Choice Hotels',
      },
      {
        value: HotelChoice.Hilton,
        label: 'Hilton Hotels & Resorts',
      },
      {
        value: HotelChoice.Hyatt,
        label: 'Hyatt Hotels',
      },
      {
        value: HotelChoice.Ihg,
        label: 'IHG Hotels & Resorts',
      },
      {
        value: HotelChoice.Marriott,
        label: 'Marriott Hotels',
      },
      {
        value: HotelChoice.Other,
        label: 'Other',
      },
    ],
  },
  [Input.HowLongToPayOffDebt]: {
    options: [
      {
        value: HowLongToPayOffDebt.LessThan18Months,
        label: 'Less than 18 months',
      },
      {
        value: HowLongToPayOffDebt.MoreThan18Months,
        label: '18 months or more',
      },
    ],
  },
  [Input.InternationalTravel]: YES_NO_OPTIONS,
  [Input.IsBuildingCredit]: {
    options: [
      {
        value: true,
        label: 'Yes — I don’t have much credit history',
      },
      {
        value: false,
        label: 'No — I want to improve my score',
      },
    ],
  },
  [Input.IsDebtCreditCard]: YES_NO_OPTIONS,
  [Input.LuxuryPerks]: ({ specificAirlineOrHotel }) => ({
    multiSelect: true,
    maxSelectedOptions: 2,
    options: [
      {
        value: LuxuryPerks.HasAirportLoungeAccess,
        label: 'Airport Lounge Access',
      },
      {
        value: LuxuryPerks.HasExpeditedSecurity,
        label: 'Expedited Security (TSA/Global Entry/Clear)',
      },
      { value: LuxuryPerks.HasTravelCredits, label: 'Travel credits' },
      {
        value: LuxuryPerks.HasPrimaryRentalCarInsurance,
        label: 'Primary car rental insurance',
      },
      ...(specificAirlineOrHotel === SpecificAirlineOrHotelValues.YesAirline ||
      specificAirlineOrHotel === SpecificAirlineOrHotelValues.YesBoth
        ? [
            {
              value: LuxuryPerks.HasFreeCheckedBags,
              label: 'Free checked bags',
            },
            {
              value: LuxuryPerks.HasCompanionFareAnnualFlight,
              label: 'Annual companion fare credit',
            },
          ]
        : []),
      ...(specificAirlineOrHotel === SpecificAirlineOrHotelValues.YesHotel ||
      specificAirlineOrHotel === SpecificAirlineOrHotelValues.YesBoth
        ? [
            {
              value: LuxuryPerks.HasFreeAnnualHotelNight,
              label: 'Annual free night hotel stay',
            },
            {
              value: LuxuryPerks.HasAutomaticEliteStatus,
              label: 'Automatic elite status w/ your preferred hotel chain',
            },
          ]
        : []),
    ],
  }),
  [Input.PrimaryGoal]: (inputs) => {
    return {
      options: [
        {
          value: PrimaryGoal.Travel,
          label: 'I want travel rewards and perks',
        },
        {
          value: PrimaryGoal.CashBack,
          label: 'I want to earn cash back on purchases',
        },
        { value: PrimaryGoal.Debt, label: 'I want to pay down debt' },
        {
          value: PrimaryGoal.BigPurchase,
          label: 'I want to make a big purchase',
        },
        ...(inputs?.[Input.CreditScore] === CreditScore.Average
          ? [
              {
                value: PrimaryGoal.BuildCredit,
                label: 'I want to build my credit',
              },
            ]
          : []),
      ],
    };
  },
  [Input.RewardsAndPerks]: {
    options: [
      {
        value: RewardsAndPerks.HasEarningRewardsRedeemedForTravelExpenses,
        label: 'Earning rewards that can be redeemed for travel expenses',
      },
      {
        value: RewardsAndPerks.HasLuxuryPerksTravelPleasant,
        label: 'Luxury perks that make travel more pleasant',
      },
      {
        value: RewardsAndPerks.HasMixRewardsAndTravelPerks,
        label: 'A mix of rewards and travel perks',
      },
    ],
  },
  [Input.CashBackBigPurchase]: {
    options: [
      {
        value: CashBackBigPurchase.Yes,
        label: 'Yes, a 0% interest period would help',
      },
      {
        value: CashBackBigPurchase.No,
        label: 'No, a 0% interest period isn’t a priority',
      },
    ],
  },
  [Input.CashBackPreference]: {
    options: [
      {
        value: CashBackPreference.SameRate,
        label: 'Using one card that earns rewards on all spending',
      },
      {
        value: CashBackPreference.Categories,
        label: 'Maximizing my rewards based on specific spending categories',
      },
    ],
  },
  [Input.ManagingCategories]: {
    options: [
      {
        value: ManagingCategories.DynamicCategories,
        label:
          'I’ll choose and track my spending categories to maximize rewards',
      },
      {
        value: ManagingCategories.StaticCategories,
        label: 'I want a card with categories that don’t change',
      },
    ],
  },
  [Input.MonthlySpend]: undefined,
  [Input.SecurityDeposit]: undefined,
  [Input.SpendingCategories]: (inputs) => ({
    multiSelect: true,
    maxSelectedOptions: 3,
    options:
      inputs.primaryGoal === PrimaryGoal.CashBack
        ? [
            {
              value: SpendingCategories.Groceries,
              label: 'Groceries',
            },
            {
              value: SpendingCategories.Gas,
              label: 'Gas',
            },
            {
              value: SpendingCategories.Restaurants,
              label: 'Restaurants',
            },
            {
              value: SpendingCategories.Travel,
              label: 'Travel',
            },
            {
              value: SpendingCategories.Entertainment,
              label: 'Entertainment',
            },
            {
              value: SpendingCategories.Streaming,
              label: 'Streaming',
            },
            {
              value: SpendingCategories.OnlineShopping,
              label: 'Online shopping',
            },
            {
              value: SpendingCategories.HomeImprovement,
              label: 'Home improvement',
            },
            {
              value: SpendingCategories.DrugStores,
              label: 'Drugstores',
            },
          ]
        : [
            {
              value: SpendingCategories.Travel,
              label: 'Travel',
            },
            {
              value: SpendingCategories.Restaurants,
              label: 'Dining',
            },
            {
              value: SpendingCategories.Groceries,
              label: 'Groceries',
            },
            {
              value: SpendingCategories.Streaming,
              label: 'Streaming services',
            },
            {
              value: SpendingCategories.Gas,
              label: 'Gas',
            },
            {
              value: SpendingCategories.DrugStores,
              label: 'Drugstores',
            },
          ],
  }),
  [Input.SpecificAirlineOrHotel]: {
    options: [
      {
        value: SpecificAirlineOrHotelValues.YesAirline,
        label: 'Yes, airline',
      },
      {
        value: SpecificAirlineOrHotelValues.YesHotel,
        label: 'Yes, hotel',
      },
      {
        value: SpecificAirlineOrHotelValues.YesBoth,
        label: 'Yes, both airline and hotel',
      },
      {
        value: SpecificAirlineOrHotelValues.Neither,
        label: 'Neither',
      },
    ],
  },
  [Input.IntroAPROrRewards]: {
    options: [
      {
        value: IntroAPROrRewards.IntroAPR,
        label: 'Longest 0% intro APR period possible so I can save on interest',
      },
      {
        value: IntroAPROrRewards.Rewards,
        label: 'Ability to earn rewards on purchases too',
      },
    ],
  },
  [Input.PurchaseSpend]: undefined,
  [Input.TypeOfDebt]: {
    options: [
      {
        value: TypeOfDebt.PersonalLoan,
        label: 'Personal Loan',
      },
      {
        value: TypeOfDebt.AutoLoan,
        label: 'Auto Loan',
      },
      {
        value: TypeOfDebt.StudentLoan,
        label: 'Student Loan',
      },
      {
        value: TypeOfDebt.HomeEquityLoan,
        label: 'Home Equity Loan',
      },
    ],
  },
  [Input.StudentSpendingCategories]: (inputs) => ({
    multiSelect: true,
    maxSelectedOptions: 2,
    options: [
      {
        value: StudentSpendingCategories.Gas,
        label: 'Gas',
      },
      {
        value: StudentSpendingCategories.Groceries,
        label: 'Groceries',
      },
      {
        value: StudentSpendingCategories.Restaurants,
        label: 'Restaurants',
      },
      {
        value: StudentSpendingCategories.Streaming,
        label: 'Streaming',
      },
      {
        value: StudentSpendingCategories.Travel,
        label: 'Travel',
      },
    ],
  }),
  [Input.PurchaseSpendingCategories]: (inputs) => ({
    multiSelect: true,
    options: [
      {
        value: PurchaseSpendingCategories.HomeImprovement,
        label: 'Home Improvement',
      },
      {
        value: PurchaseSpendingCategories.Travel,
        label: 'Travel',
      },
      {
        value: PurchaseSpendingCategories.OnlineShopping,
        label: 'Online Shopping',
      },
      {
        value: PurchaseSpendingCategories.Entertainment,
        label: 'Entertainment',
      },
      {
        value: PurchaseSpendingCategories.Other,
        label: 'Other',
      },
    ],
  }),
  [Input.FullDebt]: undefined,
};

const determineAnnualFeeAffirmativeOption = (
  inputs: InputsReducer,
  experiments?: ExperimentsReducer
) => {
  const defaultOption = 'Sure, if the rewards are worth it';

  if (inputs[Input.PrimaryGoal] === PrimaryGoal.BuildCredit) {
    if (
      experiments !== undefined &&
      isNewBuildCreditFlowExperimentActive(experiments)
    )
      return 'Yes, if it helps me get a card';

    return 'Sure, if it’s low';
  }
  return defaultOption;
};

const displayInternationalAirlines = (
  inputs: InputsReducer,
  experiments?: ExperimentsReducer
) => {
  let options: InputOptions[] = [];
  if (
    isShowInternationalPartnersExperimentsActive(experiments ?? {}) &&
    inputs.internationalTravel
  ) {
    options = [
      {
        value: AirlineChoice.AerLingus,
        label: 'Aer Lingus',
      },
      {
        value: AirlineChoice.Aeroplan,
        label: 'Aeroplan',
      },
      {
        value: AirlineChoice.BritishAirways,
        label: 'British Airways',
      },
      {
        value: AirlineChoice.Iberia,
        label: 'Iberia',
      },
    ];
  }
  return options;
};

const getOptionValue = (
  option: OptionsLookupValue,
  inputs: InputsReducer,
  experiments?: ExperimentsReducer
): InputProps => {
  if (typeof option === 'object') {
    return option;
  }
  return typeof option === 'function'
    ? option(inputs, experiments)
    : { options: [] };
};

export const getInputProps = (
  input: Input,
  inputs: InputsReducer,
  experiments?: ExperimentsReducer
): InputProps => getOptionValue(OPTIONS_LOOKUP[input], inputs, experiments);
